/**
 * Design System — все компоненты из шаблона MUI Dashboard, которые можно использовать.
 * Одна страница с демо каждого компонента.
 */
import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';

import StatCard from '../mui-dashboard/components/StatCard';
import SessionsChart from '../mui-dashboard/components/SessionsChart';
import PageViewsBarChart from '../mui-dashboard/components/PageViewsBarChart';
import HighlightedCard from '../mui-dashboard/components/HighlightedCard';
import PowerbanksStatusesDonut from '../mui-dashboard/components/PowerbanksStatusesDonut';
import PowerbanksConditionsDonut from '../mui-dashboard/components/PowerbanksConditionsDonut';
import LostPowerbanksLineChart from '../mui-dashboard/components/LostPowerbanksLineChart';
import LostPowerbanksSummaryDonut from '../mui-dashboard/components/LostPowerbanksSummaryDonut';
import DashboardBarChart from '../mui-dashboard/components/DashboardBarChart';
import MenuButton from '../mui-dashboard/components/MenuButton';
import Search from '../mui-dashboard/components/Search';
import ChartUserByCountry from '../mui-dashboard/components/ChartUserByCountry';
import CustomizedTreeView from '../mui-dashboard/components/CustomizedTreeView';
import CustomizedDataGrid from '../mui-dashboard/components/CustomizedDataGrid';
import CustomDatePicker from '../mui-dashboard/components/CustomDatePicker';
import CardAlert from '../mui-dashboard/components/CardAlert';
import Copyright from '../mui-dashboard/internals/components/Copyright';

const MOCK_STAT = {
  title: 'Stations',
  value: '42',
  interval: 'Last 30 days',
  trend: 'up',
  data: [20, 24, 22, 26, 28, 30, 32, 34, 36, 38, 40, 42, 40, 38, 42, 44, 46, 48, 50, 48, 52, 54, 52, 50, 48, 52, 54, 56, 58, 60],
};

const MOCK_STATUSES = {
  inStation: 120,
  inRent: 45,
  atAdmin: 5,
  atMerchant: 8,
  atSuperAdmin: 2,
  lost: 3,
};

const MOCK_CONDITIONS = {
  replacementRequired: 10,
  requiredToCheck: 25,
  working: 140,
};

const MOCK_LOST_POWERBANKS = {
  '2024-11-01': 0,
  '2024-11-05': 1,
  '2024-11-10': 0,
  '2024-11-15': 2,
  '2024-11-20': 1,
};

const MOCK_UTILIZATION = {
  categories: ['Station A', 'Station B', 'Station C'],
  series: [
    { name: 'Used', data: [70, 50, 90] },
    { name: 'Free', data: [30, 50, 10] },
  ],
};

const MOCK_CREDIT_DEBIT = {
  categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May'],
  series: [
    { name: 'Credit', data: [100, 150, 120, 180, 200] },
    { name: 'Debit', data: [80, 90, 100, 110, 120] },
  ],
};

function ComponentShowcase({ title, description, children }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom display="block">
          {title}
        </Typography>
        {description && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {description}
          </Typography>
        )}
        <Box sx={{ minHeight: 80 }}>{children}</Box>
      </CardContent>
    </Card>
  );
}

export default function DesignSystemPage() {
  const [selectedMonth, setSelectedMonth] = useState(10);
  const [selectedYear, setSelectedYear] = useState(2024);
  const [searchValue, setSearchValue] = useState('');

  return (
    <Box sx={{ width: '100%', maxWidth: 1600 }}>
      <Typography component="h1" variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
        Design System
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Компоненты из шаблона MUI Dashboard (@mui/material + @mui/x-charts), которые можно использовать в проекте.
      </Typography>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, mt: 3 }}>
        Карточки и метрики
      </Typography>
      <Grid container spacing={2} columns={12} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <ComponentShowcase
            title="StatCard"
            description="Карточка метрики со спарк-линией и трендом. Props: title, value, interval, trend, data."
          >
            <StatCard {...MOCK_STAT} />
          </ComponentShowcase>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <ComponentShowcase
            title="HighlightedCard"
            description="Карточка с CTA (Explore your data / Get insights)."
          >
            <HighlightedCard />
          </ComponentShowcase>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <ComponentShowcase
            title="Card + Chip + Typography"
            description="MUI Card, Chip, Typography — базовые блоки."
          >
            <Card variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="body2">Текст в карточке</Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Chip label="Success" color="success" size="small" />
                <Chip label="Default" size="small" />
              </Box>
              <Button size="small" sx={{ mt: 1 }}>Action</Button>
            </Card>
          </ComponentShowcase>
        </Grid>
      </Grid>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, mt: 3 }}>
        Графики (MUI X Charts)
      </Typography>
      <Grid container spacing={2} columns={12} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ComponentShowcase
            title="SessionsChart"
            description="LineChart (stacked area) — сессии по дням. Демо-данные."
          >
            <SessionsChart />
          </ComponentShowcase>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ComponentShowcase
            title="PageViewsBarChart"
            description="BarChart (stacked) — просмотры и загрузки по месяцам."
          >
            <PageViewsBarChart />
          </ComponentShowcase>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ComponentShowcase
            title="Users by country (ChartUserByCountry)"
            description="Donut с итогом в центре (98.5K Total) + список стран с флагами и progress bar (India, USA, Brazil, Other). PieChart + LinearProgress. Компонент из превью шаблона."
          >
            <ChartUserByCountry />
          </ComponentShowcase>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ComponentShowcase
            title="PowerbanksStatusesDonut"
            description="PieChart (donut). Props: dataStatuses (inStation, inRent, atAdmin, atMerchant, atSuperAdmin, lost)."
          >
            <PowerbanksStatusesDonut dataStatuses={MOCK_STATUSES} />
          </ComponentShowcase>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ComponentShowcase
            title="PowerbanksConditionsDonut"
            description="PieChart (donut). Props: dataStatuses (replacementRequired, requiredToCheck, working)."
          >
            <PowerbanksConditionsDonut dataStatuses={MOCK_CONDITIONS} />
          </ComponentShowcase>
        </Grid>
        <Grid size={{ xs: 12, lg: 8 }}>
          <ComponentShowcase
            title="LostPowerbanksLineChart"
            description="LineChart по дням + селекты месяц/год. Props: selectedMonth, selectedYear, onMonthChange, onYearChange, lostPowerBanks."
          >
            <LostPowerbanksLineChart
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              onMonthChange={setSelectedMonth}
              onYearChange={setSelectedYear}
              lostPowerBanks={MOCK_LOST_POWERBANKS}
            />
          </ComponentShowcase>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <ComponentShowcase
            title="LostPowerbanksSummaryDonut"
            description="Donut: остальные потерянные vs за период. Props: dataStatuses, lostPowerBanks."
          >
            <LostPowerbanksSummaryDonut
              dataStatuses={{ lost: 10 }}
              lostPowerBanks={MOCK_LOST_POWERBANKS}
            />
          </ComponentShowcase>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ComponentShowcase
            title="DashboardBarChart (vertical)"
            description="BarChart. Props: title, dataValues (series, categories), height, stacked, horizontal."
          >
            <DashboardBarChart
              title="Credit / Debit"
              dataValues={MOCK_CREDIT_DEBIT}
              height={220}
              stacked={false}
            />
          </ComponentShowcase>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ComponentShowcase
            title="DashboardBarChart (horizontal, stacked)"
            description="Горизонтальный stacked bar. horizontal=true."
          >
            <DashboardBarChart
              title="Utilization"
              dataValues={MOCK_UTILIZATION}
              height={160}
              stacked
              horizontal
            />
          </ComponentShowcase>
        </Grid>
      </Grid>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, mt: 3 }}>
        Компоненты из превью (MainGrid)
      </Typography>
      <Grid container spacing={2} columns={12} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ComponentShowcase
            title="CustomizedTreeView (Product tree)"
            description="RichTreeView с кастомными иконками и анимацией (Website, Store, Contact, Help). @mui/x-tree-view."
          >
            <CustomizedTreeView />
          </ComponentShowcase>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ComponentShowcase
            title="CustomDatePicker"
            description="DatePicker в виде кнопки с иконкой календаря. @mui/x-date-pickers + dayjs."
          >
            <CustomDatePicker />
          </ComponentShowcase>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <ComponentShowcase
            title="CustomizedDataGrid"
            description="DataGrid с чекбоксами, пагинацией, компактной плотностью. @mui/x-data-grid, данные из gridData."
          >
            <Box sx={{ height: 320 }}>
              <CustomizedDataGrid />
            </Box>
          </ComponentShowcase>
        </Grid>
      </Grid>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, mt: 3 }}>
        Элементы интерфейса
      </Typography>
      <Grid container spacing={2} columns={12} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <ComponentShowcase
            title="MenuButton"
            description="IconButton с опциональным Badge (showBadge). Используется в сайдбаре и AppBar."
          >
            <MenuButton aria-label="menu">
              <MenuRoundedIcon />
            </MenuButton>
            <MenuButton showBadge aria-label="notifications" sx={{ ml: 1 }}>
              <MenuRoundedIcon />
            </MenuButton>
          </ComponentShowcase>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <ComponentShowcase
            title="Search"
            description="Поле поиска (OutlinedInput + иконка). Props: value, onChange, onSearch, placeholder."
          >
            <Search
              value={searchValue}
              onChange={setSearchValue}
              onSearch={() => {}}
              placeholder="Search…"
            />
          </ComponentShowcase>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <ComponentShowcase
            title="CardAlert"
            description="Карточка-алерт (Plan about to expire, скидка). Сейчас скрыта в сайдбаре."
          >
            <CardAlert />
          </ComponentShowcase>
        </Grid>
      </Grid>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, mt: 3 }}>
        Прочее
      </Typography>
      <Grid container spacing={2} columns={12} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12 }}>
          <ComponentShowcase
            title="Copyright"
            description="Текст копирайта с ссылкой. Используется в MainGrid внизу."
          >
            <Copyright />
          </ComponentShowcase>
        </Grid>
      </Grid>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, mb: 4 }}>
        Все компоненты с превью Dashboard: StatCard, HighlightedCard, SessionsChart, PageViewsBarChart, Users by country (ChartUserByCountry), донаты и бары пауэрбанков, CustomizedTreeView, CustomizedDataGrid, CustomDatePicker, MenuButton, Search, CardAlert, Copyright. В layout: SideMenu, SideMenuMobile, AppNavbar, OptionsMenu.
      </Typography>
    </Box>
  );
}
